import ApiService from "@/core/services/api.service";

/**
 * Workbench Service — front-end seam for the underwriter work queue.
 *
 * LIVE (default): `getWorkQueue()` calls the read-only server endpoint
 * `GET /workbench/queue`, which assembles the queue from Zoho and returns the
 * identical WorkItem row model documented below.
 *
 * DEMO (`getWorkQueue({ demo: true })`, driven by the `?demo=1` route query): returns
 * the hard-coded synthetic rows below so the page can be shown with no backend.
 *
 * The mock's row shape is the contract the live implementation honours:
 *   - The Operational Queue is materialised on Zoho `K_IbaisTransactions`
 *     (COQL against `K_IbaisTransactions`, joined to `K_Policies` /
 *     `K_Clients` / `K_Subagents` for the display fields).
 *   - The INSIGHT readiness flags (insightClient / posted / stampedBack) are
 *     derived from the INSIGHT leg (Client / Subagent / PolicyTransaction) and
 *     the stamp-back fields on `K_IbaisTransactions` (`K_Insight*`,
 *     `Txn_Workflow_Status`, `Posting_Ready_Status`).
 * The shape returned here is the contract the live implementation must honour.
 *
 * NO real client data. Client names are placeholders ("DEV TEST CLIENT A").
 * Premium figures are synthetic.
 */

/**
 * The eight Operational Queue statuses, in operational order.
 * Kept here so the UI and the (future) live query share one vocabulary.
 * @type {string[]}
 */
export const QUEUE_STATUSES = [
  "Ready to Prepare",
  "Missing Evidence",
  "Fleet Uploaded Not Processed",
  "Pricing Review",
  "Ready to Post",
  "Posted Awaiting Readback",
  "Stamped Back",
  "Exception"
];

/**
 * @typedef {Object} ReadinessFlag
 * @property {boolean} ok
 * @property {string} [detail] short tooltip explanation
 *
 * @typedef {Object} Readiness
 * @property {ReadinessFlag} insightClient  INSIGHT Client record resolved
 * @property {ReadinessFlag} subagent       Producing broker / subagent linked
 * @property {ReadinessFlag} policy         INSIGHT Policy exists
 * @property {ReadinessFlag} priced         Premium rated / priced
 * @property {ReadinessFlag} posted         Posted to INSIGHT (invoice number)
 * @property {ReadinessFlag} stampedBack    Invoice / txn id stamped back to Zoho
 *
 * @typedef {Object} NextAction
 * @property {string} key      stable action key
 * @property {string} label    button label
 * @property {string} [route]  Vue route to navigate to; omit for not-yet-wired actions
 *
 * @typedef {Object} WorkItem
 * @property {string} id
 * @property {string} client
 * @property {string} policyNumber
 * @property {string} brand
 * @property {string} txnType
 * @property {string} status  one of QUEUE_STATUSES
 * @property {string} effectiveDate  ISO date (YYYY-MM-DD)
 * @property {number} [premium]  synthetic, in AUD
 * @property {Readiness} readiness
 * @property {NextAction} nextAction
 */

const ok = detail => ({ ok: true, detail });
const no = detail => ({ ok: false, detail });

/**
 * @type {WorkItem[]}
 */
const MOCK_WORK_QUEUE = [
  {
    id: "WB-1001",
    client: "DEV TEST CLIENT A",
    policyNumber: "FLT-100001",
    brand: "Fuse Fleet",
    txnType: "New Business",
    status: "Ready to Prepare",
    effectiveDate: "2026-07-15",
    readiness: {
      insightClient: no("No INSIGHT Client resolved yet"),
      subagent: ok("Linked to producing broker"),
      policy: no("INSIGHT Policy not created"),
      priced: ok("Rated in FinRater"),
      posted: no("Not posted"),
      stampedBack: no("Nothing to stamp back")
    },
    nextAction: { key: "prepare-insight", label: "Prepare Transaction" }
  },
  {
    id: "WB-1002",
    client: "DEV TEST CLIENT B",
    policyNumber: "FLT-100002",
    brand: "Fuse Fleet",
    txnType: "Renewal",
    status: "Ready to Prepare",
    effectiveDate: "2026-08-01",
    premium: 48250,
    readiness: {
      insightClient: ok("INSIGHT Client CL-55021"),
      subagent: ok("Linked to producing broker"),
      policy: no("Take-up policy not created"),
      priced: ok("Rated in FinRater"),
      posted: no("Not posted"),
      stampedBack: no("Nothing to stamp back")
    },
    nextAction: { key: "prepare-insight", label: "Prepare Transaction" }
  },
  {
    id: "WB-1003",
    client: "DEV TEST CLIENT C",
    policyNumber: "FLT-100003",
    brand: "Rentsure",
    txnType: "New Business",
    status: "Missing Evidence",
    effectiveDate: "2026-07-20",
    readiness: {
      insightClient: no("Blocked pending evidence"),
      subagent: ok("Linked to producing broker"),
      policy: no("INSIGHT Policy not created"),
      priced: no("Cannot price without fleet schedule"),
      posted: no("Not posted"),
      stampedBack: no("Nothing to stamp back")
    },
    nextAction: { key: "request-evidence", label: "Request Evidence" }
  },
  {
    id: "WB-1004",
    client: "DEV TEST CLIENT D",
    policyNumber: "FLT-100004",
    brand: "Fuse Fleet",
    txnType: "Endorsement",
    status: "Fleet Uploaded Not Processed",
    effectiveDate: "2026-07-10",
    readiness: {
      insightClient: ok("INSIGHT Client CL-55044"),
      subagent: ok("Linked to producing broker"),
      policy: ok("INSIGHT Policy PO-90014"),
      priced: no("Awaiting fleet processing"),
      posted: no("Not posted"),
      stampedBack: no("Nothing to stamp back")
    },
    nextAction: { key: "process-fleet", label: "Process Fleet" }
  },
  {
    id: "WB-1005",
    client: "DEV TEST CLIENT E",
    policyNumber: "FLT-100005",
    brand: "Rentsure",
    txnType: "New Business",
    status: "Pricing Review",
    effectiveDate: "2026-07-25",
    premium: 112400,
    readiness: {
      insightClient: ok("INSIGHT Client CL-55051"),
      subagent: ok("Linked to producing broker"),
      policy: ok("INSIGHT Policy PO-90021"),
      priced: no("Referred — awaiting UW pricing sign-off"),
      posted: no("Not posted"),
      stampedBack: no("Nothing to stamp back")
    },
    nextAction: { key: "review-pricing", label: "Review Pricing" }
  },
  {
    id: "WB-1006",
    client: "DEV TEST CLIENT F",
    policyNumber: "FLT-100006",
    brand: "Fuse Fleet",
    txnType: "Renewal",
    status: "Ready to Post",
    effectiveDate: "2026-08-05",
    premium: 67890,
    readiness: {
      insightClient: ok("INSIGHT Client CL-55066"),
      subagent: ok("Linked to producing broker"),
      policy: ok("INSIGHT Policy PO-90033"),
      priced: ok("Priced and prepared"),
      posted: no("Awaiting human POST"),
      stampedBack: no("Nothing to stamp back")
    },
    nextAction: {
      key: "review-and-post",
      label: "Review & Post",
      route: "/billing-processing/WB-1006"
    }
  },
  {
    id: "WB-1007",
    client: "DEV TEST CLIENT G",
    policyNumber: "FLT-100007",
    brand: "Rentsure",
    txnType: "New Business",
    status: "Ready to Post",
    effectiveDate: "2026-07-30",
    premium: 25100,
    readiness: {
      insightClient: ok("INSIGHT Client CL-55077"),
      subagent: ok("Linked to producing broker"),
      policy: ok("INSIGHT Policy PO-90041"),
      priced: ok("Priced and prepared"),
      posted: no("Awaiting human POST"),
      stampedBack: no("Nothing to stamp back")
    },
    nextAction: {
      key: "review-and-post",
      label: "Review & Post",
      route: "/billing-processing/WB-1007"
    }
  },
  {
    id: "WB-1008",
    client: "DEV TEST CLIENT H",
    policyNumber: "FLT-100008",
    brand: "Fuse Fleet",
    txnType: "New Business",
    status: "Posted Awaiting Readback",
    effectiveDate: "2026-07-12",
    premium: 89300,
    readiness: {
      insightClient: ok("INSIGHT Client CL-55088"),
      subagent: ok("Linked to producing broker"),
      policy: ok("INSIGHT Policy PO-90052"),
      priced: ok("Priced and prepared"),
      posted: ok("Posted — awaiting invoice number read-back"),
      stampedBack: no("Not stamped back yet")
    },
    nextAction: { key: "read-back", label: "Read Back Invoice" }
  },
  {
    id: "WB-1009",
    client: "DEV TEST CLIENT I",
    policyNumber: "FLT-100009",
    brand: "Rentsure",
    txnType: "Renewal",
    status: "Stamped Back",
    effectiveDate: "2026-06-30",
    premium: 54600,
    readiness: {
      insightClient: ok("INSIGHT Client CL-55099"),
      subagent: ok("Linked to producing broker"),
      policy: ok("INSIGHT Policy PO-90063"),
      priced: ok("Priced and prepared"),
      posted: ok("Posted — invoice INV-778120"),
      stampedBack: ok("Invoice + txn id stamped back to Zoho")
    },
    nextAction: { key: "view-policy", label: "View Policy" }
  },
  {
    id: "WB-1010",
    client: "DEV TEST CLIENT J",
    policyNumber: "FLT-100010",
    brand: "Fuse Fleet",
    txnType: "Endorsement",
    status: "Exception",
    effectiveDate: "2026-07-18",
    premium: 15750,
    readiness: {
      insightClient: ok("INSIGHT Client CL-55100"),
      subagent: no("Producing broker mismatch vs INSIGHT Subagent"),
      policy: ok("INSIGHT Policy PO-90074"),
      priced: ok("Priced and prepared"),
      posted: no("POST rejected by INSIGHT"),
      stampedBack: no("Blocked by exception")
    },
    nextAction: { key: "resolve-exception", label: "Resolve Exception" }
  }
];

/**
 * Fetch the underwriter work queue.
 *
 * LIVE (default): reads the read-only server endpoint `GET /workbench/queue`, which
 * assembles the queue from `K_IbaisTransactions` (Ledger_Source in {ZOHO_DRAFT, INSIGHT})
 * joined to policy/client/subagent, enriched with the INSIGHT-derived readiness flags.
 *
 * DEMO: when `options.demo` is true (route `?demo=1`), resolves the synthetic mock rows
 * after a short delay to mimic a network read - no backend required.
 *
 * P7-B: `options.order === 'cash-first'` requests the migration ordering (largest cash first). The
 * SERVER is the authoritative comparator (workbench-service.js orderWorkItems) - we only pass the
 * mode through as `?order=cash-first`. In DEMO mode there is no server, so the mock is ordered by a
 * minimal magnitude-desc/oldest-first display sort (clearly demo-only; the live path never uses it).
 *
 * @param {{demo?: boolean, order?: string}} [options]
 * @returns {Promise<WorkItem[]>}
 */
export function getWorkQueue(options) {
  const demo = !!(options && options.demo);
  const cashFirst = !!(options && options.order === "cash-first");
  if (demo) {
    return new Promise(resolve => {
      const rows = JSON.parse(JSON.stringify(MOCK_WORK_QUEUE));
      setTimeout(() => resolve(cashFirst ? sortMockCashFirst(rows) : rows), 300);
    });
  }
  const query = cashFirst ? "?order=cash-first" : "";
  return ApiService.get(`/workbench/queue${query}`).then(({ data }) => data.result);
}

/**
 * Workbench 2.0 unified work list (ledger tickets + accepted-transaction gaps).
 * @param {{q?: string, nextStep?: string, order?: string}} [options]
 * @returns {Promise<Object[]>}
 */
export function getWorkbenchWork(options) {
  const params = new URLSearchParams();
  if (options && options.q) params.set("q", String(options.q));
  if (options && options.nextStep) params.set("nextStep", String(options.nextStep));
  if (options && options.order === "cash-first") params.set("order", "cash-first");
  const qs = params.toString();
  return ApiService.get(`/workbench/work${qs ? `?${qs}` : ""}`).then(({ data }) => data.result);
}

/**
 * Human-gated mint of a ZOHO_DRAFT billing ticket from an accepted transaction id.
 * @param {string} transactionId
 * @returns {Promise<{ledgerRowId?: string, created?: boolean, detail?: string, error?: string}>}
 */
export function createBillingWork(transactionId) {
  return ApiService.post(`/workbench/actions/create-billing-work/${transactionId}`).then(
    ({ data }) => data.result
  );
}

export function getBrokerLinkStatus(clientId, options = {}) {
  const params = new URLSearchParams();
  if (options.transactionId) params.set("transactionId", String(options.transactionId));
  if (options.rowSubagentId) params.set("rowSubagentId", String(options.rowSubagentId));
  if (options.policyId) params.set("policyId", String(options.policyId));
  const qs = params.toString();
  return ApiService.get(
    `/workbench/clients/${clientId}/broker-link-status${qs ? `?${qs}` : ""}`
  ).then(({ data }) => data.result);
}

export function linkClientBroker(clientId, body = {}) {
  return ApiService.post(`/workbench/clients/${clientId}/link-broker`, body).then(({ data }) => data.result);
}

export function ensureBrokerGold(clientId, body = {}) {
  return ApiService.post(`/workbench/clients/${clientId}/ensure-broker-gold`, body).then(
    ({ data }) => data.result
  );
}

export function ensureClientGold(clientId, body = {}) {
  return ApiService.post(`/workbench/clients/${clientId}/ensure-client-gold`, body).then(
    ({ data }) => data.result
  );
}

export function fixClientLink(clientId, body = {}) {
  return ApiService.post(`/workbench/clients/${clientId}/fix-link`, body).then(({ data }) => data.result);
}

export function searchInsightProducingBrokers(query) {
  const q = encodeURIComponent(query || "");
  return ApiService.get(`/workbench/producing-brokers/insight-search?q=${q}`).then(
    ({ data }) => data.result
  );
}

export function getInsightBrokerCreateCapability() {
  return ApiService.get(`/workbench/producing-brokers/insight-create-capability`).then(
    ({ data }) => data.result
  );
}

/**
 * DEMO-ONLY display ordering for the synthetic mock (there is no server to order it). Mirrors the
 * server rule at a glance - largest premium magnitude first, a row with no premium last, oldest
 * effectiveDate breaking ties - but is NOT the authoritative comparator (that lives server-side in
 * SERVER/services/workbench-service.js). Never reached on the live path.
 */
function sortMockCashFirst(rows) {
  const magnitude = r => (r && typeof r.premium === "number" && r.premium !== 0 ? Math.abs(r.premium) : null);
  const age = r => (r && r.effectiveDate ? Date.parse(r.effectiveDate) : Number.POSITIVE_INFINITY);
  return rows.slice().sort((a, b) => {
    const ma = magnitude(a);
    const mb = magnitude(b);
    if ((ma === null) !== (mb === null)) {
      return ma === null ? 1 : -1;
    }
    if (ma !== null && mb !== null && ma !== mb) {
      return mb - ma;
    }
    const aa = age(a);
    const ab = age(b);
    if (aa !== ab) {
      return aa - ab;
    }
    return String(a.id) < String(b.id) ? -1 : String(a.id) > String(b.id) ? 1 : 0;
  });
}

/**
 * W3-A: create an INSIGHT Client for a Zoho client (the workbench's first WRITE action).
 * POSTs the read-verified server endpoint, which creates the INSIGHT Client + primary Contact
 * and stamps the ClientId back onto Zoho. The human gate is the confirm dialog in the caller;
 * this only fires after the user confirms.
 *
 * W3-FIX: the endpoint takes the WORK-ROW id (WorkItem.id, a K_IbaisTransactions ledger-row id),
 * NOT a client id. The server rebuilds the row, re-authorises the action, and derives the client
 * id from the row itself - so the FE cannot drive a create against an arbitrary client.
 *
 * Resolves to the server's shaped result: { done, insightClientId, adopted?, detail } on success,
 * or { error } for a handled degrade (action not available / already linked / no subagent / not
 * configured / create failed). Rejects only on a transport/500 failure.
 *
 * @param {string} ledgerRowId  the work-row id (WorkItem.id)
 * @returns {Promise<{done?: boolean, insightClientId?: (string|number), adopted?: boolean, detail?: string, error?: string}>}
 */
export function createInsightClient(ledgerRowId) {
  return ApiService.post(`/workbench/actions/create-insight-client/${ledgerRowId}`).then(({ data }) => data.result);
}

/**
 * W3-C: stamp a POSTED INSIGHT transaction back onto its Zoho ledger row. POSTs the server
 * endpoint, which reads the transaction back from INSIGHT and writes the reconciled figures onto
 * K_IbaisTransactions. The human gate is the confirm dialog in the caller; this only fires after
 * the user confirms. The endpoint takes the WORK-ROW id (WorkItem.id) and re-authorises the action
 * server-side (requires the row's recomputed next action to be 'stamp-back').
 *
 * Resolves to the server's shaped result: { done, exception?, exceptions?, detail } on success (a
 * stamp with exceptions still writes and reports exception:true), or { error } for a handled degrade
 * (action not available / already stamped / no txn id / not configured / read-back or write failed /
 * id mismatch). Rejects only on a transport/500 failure.
 *
 * @param {string} ledgerRowId  the work-row id (WorkItem.id)
 * @returns {Promise<{done?: boolean, exception?: boolean, exceptions?: string[], detail?: string, error?: string}>}
 */
export function stampBack(ledgerRowId) {
  return ApiService.post(`/workbench/actions/stamp-back/${ledgerRowId}`).then(({ data }) => data.result);
}

/**
 * P7-A: READ-ONLY per-family INSIGHT connection status for a work row (the Connect panel's three
 * lights + renewal/take-up badge). GETs the read-only server endpoint, which derives the client /
 * broker / policy lights from the same server-side readiness the queue uses, with an optional deep
 * verify against INSIGHT when creds are configured.
 *
 * Resolves to the server's shaped model: { found, status, blocked, deepMode, client, broker,
 * policy, renewal } on success, or { error } for a handled degrade (row not found / not an
 * operational row). Rejects only on a transport/500 failure. Each of client/broker/policy also
 * carries a `verified` truth ('live' | 'shallow' | 'anchor' | 'none' | 'failed' - Codex-H2, P7-A-FIX).
 *
 * @param {string} ledgerRowId  the work-row id (WorkItem.id / the `ledger-` route id, prefix stripped)
 * @returns {Promise<Object>}
 */
export function getInsightConnectionStatus(ledgerRowId) {
  return ApiService.get(`/workbench/connection/${ledgerRowId}`).then(({ data }) => data.result);
}

/**
 * P7-A: COMPOSITE Connect-to-INSIGHT action. POSTs the server endpoint, which orchestrates the
 * dependency chain client -> broker -> policy(take-up) by calling the existing W3 services, re-deriving
 * the authoritative next action between steps, fail-closed at the first degrade, idempotent on re-run,
 * Exception-dominant, and NEVER posting. The human gate is the confirm dialog in the caller; this only
 * fires after the user confirms.
 *
 * Resolves to the server's shaped result: { done, status?, nextAction?, steps, detail } on success, or
 * { done:false, blocked?/stoppedAt?, error, steps } for a handled degrade, or the distinct
 * { done:false, pending:true, stoppedAt, status, steps, detail } shape (Codex-H1, P7-A-FIX) when a
 * step's write succeeded but the row has not yet reflected it (read-after-write lag) - not a failure.
 * Rejects only on a transport/500 failure.
 *
 * @param {string} ledgerRowId  the work-row id (WorkItem.id / the `ledger-` route id, prefix stripped)
 * @returns {Promise<Object>}
 */
export function connectToInsight(ledgerRowId) {
  return ApiService.post(`/workbench/actions/connect-insight/${ledgerRowId}`).then(({ data }) => data.result);
}

/**
 * Read-only case evidence readiness for a ledger row (fleet/claims extraction panel).
 * Requires an explicit positive lifecycleVersion — never invents "latest".
 *
 * @param {string} ledgerRowId
 * @param {number|string} lifecycleVersion
 * @returns {Promise<Object>}
 */
export function getEvidenceReadiness(ledgerRowId, lifecycleVersion) {
  const version = encodeURIComponent(String(lifecycleVersion));
  return ApiService.get(
    `/workbench/evidence-readiness/${ledgerRowId}?lifecycleVersion=${version}`
  ).then(({ data }) => data.result);
}

/**
 * Deterministic policy-processing-readiness.v1 for a ledger row (single next step).
 * @param {string} ledgerRowId
 * @returns {Promise<Object>}
 */
export function getPolicyProcessingReadiness(ledgerRowId) {
  return ApiService.get(`/workbench/readiness/${ledgerRowId}`).then(({ data }) => data.result);
}

/**
 * Read-only Neon commercial-terms stamp status for a ledger row (Missing / Bound / Error).
 * @param {string} ledgerRowId
 * @returns {Promise<Object>}
 */
export function getCommercialTermsStampStatus(ledgerRowId) {
  return ApiService.get(
    `/workbench/commercial-terms-stamp-status/${encodeURIComponent(ledgerRowId)}`
  ).then(({ data }) => data.result);
}

/**
 * Human-gated stamp of Neon bound commercial terms under the Fuse agency policy number.
 * Soft-fails as `{ ok:false, error }` in the success envelope (same pattern as Create draft).
 * Requires an Idempotency-Key so retries do not double-stamp.
 * confirmedBoundPremiumCents must match the status/payable the operator just confirmed.
 * @param {string} ledgerRowId
 * @param {{ idempotencyKey?: string, confirmedBoundPremiumCents: number }} options
 * @returns {Promise<Object>}
 */
export function stampCommercialTerms(ledgerRowId, options) {
  const opts = options && typeof options === 'object' ? options : {};
  const key =
    String(opts.idempotencyKey || '').trim() ||
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `stamp-${Date.now()}-${Math.random().toString(16).slice(2)}`);
  const confirmedBoundPremiumCents = Number(opts.confirmedBoundPremiumCents);
  const body = { idempotencyKey: key, confirmedBoundPremiumCents };
  if (opts.pricingDecision && typeof opts.pricingDecision === 'object') {
    body.pricingDecision = opts.pricingDecision;
  }
  return ApiService.post(
    `/workbench/actions/stamp-commercial-terms/${encodeURIComponent(ledgerRowId)}`,
    body,
    { headers: { 'Idempotency-Key': key } }
  ).then(({ data }) => data.result);
}

/**
 * Replace one eligible pre-package policy projection, then run clean Stamp.
 * Requires the same quoted-term confirmation plus a no-external-bordereaux confirmation.
 */
export function reconcileEarlyPolicy(ledgerRowId, options) {
  const opts = options && typeof options === "object" ? options : {};
  const key =
    String(opts.idempotencyKey || "").trim() ||
    (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `reconcile-${Date.now()}-${Math.random().toString(16).slice(2)}`);
  const body = {
    idempotencyKey: key,
    confirmedBoundPremiumCents: Number(opts.confirmedBoundPremiumCents),
    confirmedNoExternalBordereaux: opts.confirmedNoExternalBordereaux === true,
    confirmedReplacement: opts.confirmedReplacement === true,
    expectedHeadSnapshotId: String(opts.expectedHeadSnapshotId || "").trim() || null,
    expectedHeadContentHash: String(opts.expectedHeadContentHash || "").trim() || null,
    pricingDecision:
      opts.pricingDecision && typeof opts.pricingDecision === "object"
        ? opts.pricingDecision
        : null
  };
  return ApiService.post(
    `/workbench/actions/reconcile-early-policy/${encodeURIComponent(ledgerRowId)}`,
    body,
    { headers: { "Idempotency-Key": key } }
  ).then(({ data }) => data.result);
}

/** Interim Manual IBAIS status (Rentsure / CRI). Soft-fail { ok:false, error }. */
export function getManualIbaisStatus(ledgerRowId) {
  return ApiService.get(
    `/workbench/manual-ibais/${encodeURIComponent(ledgerRowId)}`
  ).then(({ data }) => data.result);
}

export function saveManualIbaisInstruction(ledgerRowId, body) {
  return ApiService.post(
    `/workbench/actions/manual-ibais/${encodeURIComponent(ledgerRowId)}/instruction`,
    body || {}
  ).then(({ data }) => data.result);
}

export function markManualIbaisAwaiting(ledgerRowId, body) {
  return ApiService.post(
    `/workbench/actions/manual-ibais/${encodeURIComponent(ledgerRowId)}/mark-awaiting`,
    body || {}
  ).then(({ data }) => data.result);
}

export function completeManualIbais(ledgerRowId, body) {
  return ApiService.post(
    `/workbench/actions/manual-ibais/${encodeURIComponent(ledgerRowId)}/complete`,
    body || {}
  ).then(({ data }) => data.result);
}

export default {
  getWorkQueue,
  getWorkbenchWork,
  createBillingWork,
  getBrokerLinkStatus,
  linkClientBroker,
  ensureBrokerGold,
  ensureClientGold,
  getInsightBrokerCreateCapability,
  fixClientLink,
  searchInsightProducingBrokers,
  createInsightClient,
  stampBack,
  getInsightConnectionStatus,
  connectToInsight,
  getEvidenceReadiness,
  getPolicyProcessingReadiness,
  getCommercialTermsStampStatus,
  stampCommercialTerms,
  reconcileEarlyPolicy,
  getManualIbaisStatus,
  saveManualIbaisInstruction,
  markManualIbaisAwaiting,
  completeManualIbais,
  QUEUE_STATUSES
};
